import React, { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import axios from "axios";
import { API_URL, WHITELABEL_ID } from "../constants";
import { AuthContext } from "./AuthContext";
import { ConfigContext } from "./ConfigContext";

const Header = () => {
    const config = useContext(ConfigContext);
    const { token, setToken, admin, setAdmin, hasRole, hasPermission } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const toggle = () => setIsOpen(!isOpen);

    const onLogout = async () => {
        localStorage.removeItem("authToken");

        setIsLoggingOut(true); // Set logging out to true
        try {
            // Make an API call to logout from the backend
            await axios.delete(
                `${API_URL}/api/wl/${WHITELABEL_ID}/manager/logout`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Clear the token from local storage (or wherever it's stored)
            localStorage.removeItem("authToken");
            // Update the token in the AuthContext
            setToken(null);
            setAdmin(null);

            // Redirect the user to the login or home page
            navigate("/");
        } catch (error) {
            console.error("Failed to logout:", error);
            localStorage.removeItem("authToken");
            setToken(null);
            setAdmin(null);
            navigate("/");
        } finally {
            setIsLoggingOut(false);
        }
    };

    const getInitials = (name) => {
        return name
            ? name
                  .split(" ")
                  .map((part) => part[0])
                  .join("")
                  .toUpperCase()
            : "";
    };

    return (
        <nav className="navbar navbar-expand-lg navbar-light bg-light px-2">
            <Link className="navbar-brand" to="/">
                <img src={config.icons.bigIconUrl} height="50" alt="" />
            </Link>
            <button className="navbar-toggler" type="button" onClick={toggle}>
                <span className="navbar-toggler-icon" />
            </button>
            {token ? (
                <>
                    <div className={`collapse navbar-collapse ${isOpen ? "show" : ""}`}>
                        <ul className="navbar-nav">
                            <li className="nav-item">
                                <Link
                                    className={`nav-link ${
                                        location.pathname === "/dashboard" ? "active" : ""
                                    }`}
                                    to="/dashboard"
                                >
                                    Dashboard
                                </Link>
                            </li>
                            {hasPermission("pickup-view") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${
                                            location.pathname.startsWith("/pickups") ? "active" : ""
                                        }`}
                                        href="#"
                                        id="navbarDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Pickups
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li>
                                            <Link className="dropdown-item" to="/pickups/live-requests">
                                                Live Requests
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/pickups">
                                                Pickup List
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to="/pickups/statistics/by_day"
                                            >
                                                Statistics by Day
                                            </Link>
                                        </li>
                                        {hasPermission("sales-view") && (
                                            <li>
                                                <Link className="dropdown-item" to="/pickups/sales">
                                                    Sales
                                                </Link>
                                            </li>
                                        )}
                                    </ul>
                                </li>
                            )}
                            {hasPermission("driver-view") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${
                                            location.pathname.startsWith("/drivers") ||
                                            location.pathname.startsWith("/vehicles")
                                                ? "active"
                                                : ""
                                        }`}
                                        href="#"
                                        id="navbarDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Drivers
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li>
                                            <Link className="dropdown-item" to="/drivers">
                                                Driver List
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/drivers/create">
                                                Create Driver
                                            </Link>
                                        </li>
                                        {hasPermission("vehicle-view") && (
                                            <>
                                                <li>
                                                    <Link className="dropdown-item" to="/vehicles">
                                                        Vehicles
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link
                                                        className="dropdown-item"
                                                        to="/vehicles/create"
                                                    >
                                                        Create Vehicle
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to="/drivers/performance"
                                            >
                                                Performance
                                            </Link>
                                        </li>
                                        {hasPermission("driver-update") && (
                                            <>
                                                <li>
                                                    <Link
                                                        className="dropdown-item"
                                                        to="/drivers/notify-all"
                                                    >
                                                        Notify All
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </li>
                            )}
                            {hasPermission("user-view") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${
                                            location.pathname.startsWith("/users") ? "active" : ""
                                        }`}
                                        href="#"
                                        id="navbarDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Customers
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li>
                                            <Link className="dropdown-item" to="/users">
                                                Customer List
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/users/create">
                                                Create Customer
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/users/performance">
                                                Performance
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to="/users/statistics/signups/by_week"
                                            >
                                                Signups by Week
                                            </Link>
                                        </li>
                                        {hasPermission("user-update") && (
                                            <>
                                                <li>
                                                    <Link
                                                        className="dropdown-item"
                                                        to="/users/notify-all"
                                                    >
                                                        Notify All
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </li>
                            )}
                            {hasPermission("invoice-view") && (
                                <li className="nav-item">
                                    <Link
                                        className={`nav-link ${
                                            location.pathname.startsWith("/invoices")
                                                ? "active"
                                                : ""
                                        }`}
                                        to="/invoices"
                                    >
                                        Invoices
                                    </Link>
                                </li>
                            )}
                            {/*{hasPermission("cargo-insurance-view") && (*/}
                            {/*    <li className="nav-item">*/}
                            {/*        <Link className={`nav-link ${location.pathname.startsWith("/cargo-insurances") ? "active" : ""}`} to="/cargo-insurances">*/}
                            {/*            Insurances*/}
                            {/*        </Link>*/}
                            {/*    </li>*/}
                            {/*)}*/}
                            {hasPermission("truck-type-view") && (
                                <li className="nav-item">
                                    <Link
                                        className={`nav-link ${
                                            location.pathname.startsWith("/truck-types/prices")
                                                ? "active"
                                                : ""
                                        }`}
                                        to="/truck-types/prices"
                                    >
                                        Prices
                                    </Link>
                                </li>
                            )}
                            {hasPermission("tax-rate-view") && (
                                <li className="nav-item">
                                    <Link className={`nav-link ${location.pathname.startsWith("/tax-rates") ? "active" : ""}`} to="/tax-rates">
                                        Taxes
                                    </Link>
                                </li>
                            )}
                            {hasRole("super-manager") && (
                                <li className="nav-item dropdown">
                                    <a
                                        className={`nav-link dropdown-toggle ${
                                            location.pathname.startsWith("/managers")
                                                ? "active"
                                                : ""
                                        }`}
                                        href="#"
                                        id="coworkerDropdown"
                                        role="button"
                                        data-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Managers
                                    </a>
                                    <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                                        <li>
                                            <Link className="dropdown-item" to="/managers">
                                                Managers
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/managers/create">
                                                Create Manager
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="dropdown-item" to="/managers/roles">
                                                Roles
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to="/managers/roles/create"
                                            >
                                                Create Role
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to="/managers/permissions"
                                            >
                                                Permissions
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to="/managers/permissions/create"
                                            >
                                                Create Permission
                                            </Link>
                                        </li>
                                    </ul>
                                </li>
                            )}
                        </ul>
                    </div>
                    {/* Admin profile and logout dropdown */}
                    <ul className="navbar-nav ml-auto">
                        <li className="nav-item dropdown">
                            <a
                                className="nav-link dropdown-toggle d-flex align-items-center"
                                href="#"
                                id="adminDropdown"
                                role="button"
                                data-toggle="dropdown"
                                aria-expanded="false"
                            >
                                {admin?.avatar ? (
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "50%",
                                            backgroundImage: `url(${admin.avatar})`,
                                            backgroundPosition: "center",
                                            backgroundRepeat: "no-repeat",
                                            backgroundSize: "cover",
                                        }}
                                    />
                                ) : (
                                    <span className="default-avatar">
                                        {getInitials(admin?.name)}
                                    </span>
                                )}
                                &nbsp; {admin?.name}
                            </a>
                            <ul
                                className="dropdown-menu dropdown-menu-right"
                                aria-labelledby="adminDropdown"
                            >
                                <li>
                                    <Link className="dropdown-item" to="/my-profile">
                                        My Profile
                                    </Link>
                                </li>
                                {hasRole("super-manager") && (
                                    <li>
                                        <Link className="dropdown-item" to="/manage-billing">
                                            Manage Billing
                                        </Link>
                                    </li>
                                )}
                                <li>
                                    <hr className="dropdown-divider" />
                                </li>
                                <li>
                                    <button className="dropdown-item" onClick={onLogout}>
                                        {isLoggingOut ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                &nbsp;Logging out...
                                            </>
                                        ) : (
                                            "Logout"
                                        )}
                                    </button>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </>
            ) : null}
        </nav>
    );
};

export default Header;
